import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
/** Style Import */
import "./Style.css";
/** Services Import */
import Global from "../../Services/Common/Global";
import Commons from "../../Services/Common/Common";
import IndexedDb from "../../Services/IndexedDb/IndexedDb";
import TranslationService from "../../Services/Translation/TranslationService";
import EmployeeService from "../../Services/Employee/EmployeeService";
/** Components Import */
import Loading from "../../Components/Loading";
import swal from "../../Components/SweetAlert2";
import { Card, Row, Col, Badge, Modal, Button } from "react-bootstrap";
import SmsVerification from "../../Components/SmsVerification/index";
import AutoTranslate from "../../Components/AutoTranslate";
/** Icons Import */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faCalendar, faTint, faPhone, faGlobe, faBriefcase, faShieldAlt, faCamera, faEdit, faCheck, faTimes, faEnvelope } from "@fortawesome/free-solid-svg-icons";

function MyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const language = useSelector((state) => state.localStorage.language);
  const authorization = useSelector((state) => state.localStorage.authorization);
  const [userInfo, setUserInfo] = useState({});
  const [profileData, setProfileData] = useState({
    name: "",
    birthDate: "",
    bloodType: "",
    phone: "",
    nationality: "",
    eMail: "",
    concent: false,
    profileImage: null,
  });

  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isPrivacyScrolledToBottom, setIsPrivacyScrolledToBottom] = useState(false);
  const [hasEverScrolledToBottom, setHasEverScrolledToBottom] = useState(false);
  const privacyContentRef = useRef(null);

  // 사용자 정보 수정 관련 상태 추가
  const [phoneVerification, setPhoneVerification] = useState({
    isVerifying: false,
    verificationCode: "",
    isCodeSent: false,
    isVerified: false,
    countdown: 0,
    generatedCode: "", // 생성된 인증번호 저장
  });
  const [tempPhone, setTempPhone] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const [privacyTranslations, setPrivacyTranslations] = useState({
    title: "개인정보처리방침",
    section1: {
      title: "1. 개인정보의 처리 목적",
      description: "회사는 다음의 목적을 위하여 개인정보를 처리하고 있으며, 다음의 목적 이외의 용도로는 이용하지 않습니다.",
      items: ["고객 상담 및 문의 응대", "서비스 제공 및 운영", "마케팅 및 광고에의 활용", "신규 서비스 개발 및 맞춤형 서비스 제공"],
    },
    section2: {
      title: "2. 개인정보의 처리 및 보유기간",
      description: "회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.",
    },
    section3: {
      title: "3. 개인정보의 제3자 제공",
      description:
        "회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 개인정보보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.",
    },
    section4: {
      title: "4. 정보주체의 권리·의무 및 그 행사방법",
      description: "이용자는 개인정보주체로서 다음과 같은 권리를 행사할 수 있습니다.",
      items: ["개인정보 열람요구", "오류 등이 있을 경우 정정 요구", "삭제요구", "처리정지 요구"],
    },
    section5: {
      title: "5. 개인정보의 안전성 확보 조치",
      description: "회사는 개인정보보호법 제29조에 따라 다음과 같은 안전성 확보 조치를 취하고 있습니다.",
      items: ["개인정보의 암호화", "해킹 등에 대비한 기술적 대책", "개인정보에 대한 접근 제한"],
    },
  });

  // 스크롤 없이 전체가 보이는 경우(고해상도 등) 스크롤 이벤트가 없어도 동의 가능하도록 처리
  useLayoutEffect(() => {
    if (!showPrivacyModal) return;

    const applyIfNoScrollNeeded = () => {
      const el = privacyContentRef.current;
      if (!el) return;
      const { scrollHeight, clientHeight } = el;
      if (clientHeight < 1 || scrollHeight < 1) return;
      if (scrollHeight <= clientHeight + 1) {
        setHasEverScrolledToBottom(true);
      }
    };

    applyIfNoScrollNeeded();
    const raf = requestAnimationFrame(applyIfNoScrollNeeded);
    const t0 = setTimeout(applyIfNoScrollNeeded, 0);
    const t1 = setTimeout(applyIfNoScrollNeeded, 150);
    const t2 = setTimeout(applyIfNoScrollNeeded, 400);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            applyIfNoScrollNeeded();
          })
        : null;
    if (ro && privacyContentRef.current) {
      ro.observe(privacyContentRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      ro?.disconnect();
    };
  }, [showPrivacyModal, privacyTranslations]);

  const [translations, setTranslations] = useState({
    profileTitle: "내 프로필",
    name: "이름",
    birthDate: "생년월일",
    bloodType: "혈액형",
    phone: "전화번호",
    nationality: "국적",
    eMail: "이메일",
    concent: "개인정보처리방침 동의",
    agreed: "동의함",
    notAgreed: "동의하지 않음",
    // SMS 인증 관련 번역 추가
    sendVerificationCode: "인증번호 전송",
    verificationCode: "인증번호",
    verifyCode: "인증번호 확인",
    resendCode: "재전송",
    codeSent: "인증번호가 전송되었습니다",
    codeVerified: "인증이 완료되었습니다",
    codeInvalid: "인증번호가 올바르지 않습니다",
    phoneUpdateSuccess: "전화번호가 수정되었습니다",
    emailUpdateSuccess: "이메일이 수정되었습니다",
    imageUpdateSuccess: "프로필 사진이 수정되었습니다",
    updateFailed: "수정에 실패했습니다",
    // 전화번호 변경 관련 메시지 추가
    phoneNotChanged: "전화번호를 변경한 후 인증번호를 전송해주세요",
    phoneFormatInvalid: "올바른 전화번호 형식이 아닙니다",
  });

  useEffect(() => {
    Init();
  }, []);

  useEffect(() => {
    loadTranslations();
  }, [language]);

  const loadTranslations = async () => {
    try {
      const translatedTexts = await Promise.all([
        TranslationService.translateText("내 프로필", language.lang),
        TranslationService.translateText("이름", language.lang),
        TranslationService.translateText("생년월일", language.lang),
        TranslationService.translateText("혈액형", language.lang),
        TranslationService.translateText("전화번호", language.lang),
        TranslationService.translateText("국적", language.lang),
        TranslationService.translateText("이메일", language.lang),
        TranslationService.translateText("개인정보처리방침 동의", language.lang),
        TranslationService.translateText("동의함", language.lang),
        TranslationService.translateText("동의하지 않음", language.lang),
        // SMS 인증 관련 번역 추가
        TranslationService.translateText("인증번호 전송", language.lang),
        TranslationService.translateText("인증번호", language.lang),
        TranslationService.translateText("인증번호 확인", language.lang),
        TranslationService.translateText("재전송", language.lang),
        TranslationService.translateText("인증번호가 전송되었습니다", language.lang),
        TranslationService.translateText("인증이 완료되었습니다", language.lang),
        TranslationService.translateText("인증번호가 올바르지 않습니다", language.lang),
        TranslationService.translateText("전화번호가 수정되었습니다", language.lang),
        TranslationService.translateText("이메일이 수정되었습니다", language.lang),
        TranslationService.translateText("프로필 사진이 수정되었습니다", language.lang),
        TranslationService.translateText("수정에 실패했습니다", language.lang),
        // 전화번호 변경 관련 메시지 추가
        TranslationService.translateText("전화번호를 변경한 후 인증번호를 전송해주세요", language.lang),
        TranslationService.translateText("올바른 전화번호 형식이 아닙니다", language.lang),
      ]);

      setTranslations({
        profileTitle: translatedTexts[0],
        name: translatedTexts[1],
        birthDate: translatedTexts[2],
        bloodType: translatedTexts[3],
        phone: translatedTexts[4],
        nationality: translatedTexts[5],
        eMail: translatedTexts[6],
        concent: translatedTexts[7],
        agreed: translatedTexts[8],
        notAgreed: translatedTexts[9],
        // SMS 인증 관련 번역 설정
        sendVerificationCode: translatedTexts[10],
        verificationCode: translatedTexts[11],
        verifyCode: translatedTexts[12],
        resendCode: translatedTexts[13],
        codeSent: translatedTexts[14],
        codeVerified: translatedTexts[15],
        codeInvalid: translatedTexts[16],
        phoneUpdateSuccess: translatedTexts[17],
        emailUpdateSuccess: translatedTexts[18],
        imageUpdateSuccess: translatedTexts[19],
        updateFailed: translatedTexts[20],
        // 전화번호 변경 관련 메시지 설정
        phoneNotChanged: translatedTexts[21],
        phoneFormatInvalid: translatedTexts[22],
      });

      // 개인정보처리방침 번역 로드
      await loadPrivacyTranslations();
    } catch (error) {
      console.log("Translation error: ", error);
    }
  };

  const loadPrivacyTranslations = async () => {
    try {
      const privacyTexts = await Promise.all([
        TranslationService.translateText("개인정보처리방침", language.lang),
        TranslationService.translateText("1. 개인정보의 처리 목적", language.lang),
        TranslationService.translateText("회사는 다음의 목적을 위하여 개인정보를 처리하고 있으며, 다음의 목적 이외의 용도로는 이용하지 않습니다.", language.lang),
        TranslationService.translateText("고객 상담 및 문의 응대", language.lang),
        TranslationService.translateText("서비스 제공 및 운영", language.lang),
        TranslationService.translateText("마케팅 및 광고에의 활용", language.lang),
        TranslationService.translateText("신규 서비스 개발 및 맞춤형 서비스 제공", language.lang),
        TranslationService.translateText("2. 개인정보의 처리 및 보유기간", language.lang),
        TranslationService.translateText(
          "회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.",
          language.lang
        ),
        TranslationService.translateText("3. 개인정보의 제3자 제공", language.lang),
        TranslationService.translateText(
          "회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 개인정보보호법 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.",
          language.lang
        ),
        TranslationService.translateText("4. 정보주체의 권리·의무 및 그 행사방법", language.lang),
        TranslationService.translateText("이용자는 개인정보주체로서 다음과 같은 권리를 행사할 수 있습니다.", language.lang),
        TranslationService.translateText("개인정보 열람요구", language.lang),
        TranslationService.translateText("오류 등이 있을 경우 정정 요구", language.lang),
        TranslationService.translateText("삭제요구", language.lang),
        TranslationService.translateText("처리정지 요구", language.lang),
        TranslationService.translateText("5. 개인정보의 안전성 확보 조치", language.lang),
        TranslationService.translateText("회사는 개인정보보호법 제29조에 따라 다음과 같은 안전성 확보 조치를 취하고 있습니다.", language.lang),
        TranslationService.translateText("개인정보의 암호화", language.lang),
        TranslationService.translateText("해킹 등에 대비한 기술적 대책", language.lang),
        TranslationService.translateText("개인정보에 대한 접근 제한", language.lang),
      ]);

      setPrivacyTranslations({
        title: privacyTexts[0],
        section1: {
          title: privacyTexts[1],
          description: privacyTexts[2],
          items: [privacyTexts[3], privacyTexts[4], privacyTexts[5], privacyTexts[6]],
        },
        section2: {
          title: privacyTexts[7],
          description: privacyTexts[8],
        },
        section3: {
          title: privacyTexts[9],
          description: privacyTexts[10],
        },
        section4: {
          title: privacyTexts[11],
          description: privacyTexts[12],
          items: [privacyTexts[13], privacyTexts[14], privacyTexts[15], privacyTexts[16]],
        },
        section5: {
          title: privacyTexts[17],
          description: privacyTexts[18],
          items: [privacyTexts[19], privacyTexts[20], privacyTexts[21]],
        },
      });
    } catch (error) {
      console.log("Privacy translation error: ", error);
    }
  };

  const Init = async () => {
    try {
      const _userData = await IndexedDb.GetUserInfo();
      console.log("userData: ", _userData);
      setUserInfo(_userData);
      // NATIONALITY 코드 -> name 매핑
      let nationalityName = _userData.nationality;
      try {
        const resp = await Commons.Axios("get", Global.base.useUrl + Global.urls.Common.notAuthGetCode, { code: "NATIONALITY" });
        if (resp && resp.status === 200 && Array.isArray(resp.data)) {
          const match = resp.data.find((item) => item.code === _userData.nationality);
          if (match && match.name) {
            nationalityName = match.name;
          }
        }
      } catch (e) {
        console.log("Fetch nationality list error: ", e);
      }
      setProfileData({
        name: _userData.userNm,
        birthDate: Commons.DateFormating(_userData.birth, 2),
        bloodType: _userData.bloodType,
        phone: _userData.tel,
        nationality: nationalityName,
        eMail: _userData.email || _userData.eMail || "",
        concent: _userData.concent === true, // concent가 개인정보동의방침
        profileImage: _userData.photoUrl || _userData.profileImage || null,
      });
    } catch (e) {
      console.log("Init Error: ", e);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setLoading(true);

        if (file.size > 5 * 1024 * 1024) {
          alert("파일 크기는 5MB 이하여야 합니다.");
          return;
        }

        if (!file.type.startsWith("image/")) {
          alert("이미지 파일만 업로드 가능합니다.");
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        const response = await Commons.Axios("file", Global.base.useUrl + Global.urls.Content.UploadFile, {}, formData);

        if (response && response.status === 200) {
          const data = response.data || {};
          const imageUrl = data.fileUrl || data.imageUrl || data.url || data.path || (data.data && (data.data.fileUrl || data.data.imageUrl || data.data.url || data.data.path));
          if (!imageUrl) {
            console.log("Upload response (no url field)", data);
            await swal.error("업로드 응답에 이미지 경로가 없습니다.");
            return;
          }

          const upsertResp = await EmployeeService.upsertAndSync({ photoUrl: imageUrl });
          const ok = upsertResp && upsertResp.status === 200;
          if (!ok) {
            await swal.error("이미지 URL 저장에 실패했습니다.");
            return;
          }

          setProfileData((prev) => ({ ...prev, profileImage: imageUrl }));
          setUserInfo((prev) => ({ ...prev, profileImage: imageUrl }));

          event.target.value = "";
          await swal.success(translations.imageUpdateSuccess);
        } else {
          console.log("Upload failed response:", response);
          await swal.error("이미지 업로드에 실패했습니다.");
        }
      } catch (error) {
        console.log("File upload error: ", error);
        await swal.error("이미지 업로드 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleEditStart = (field, value) => {
    setEditingField(field);
    setEditValue("");
    setIsEditing(true);

    // 전화번호 편집 시작 시 SMS 인증 상태 초기화
    if (field === "phone") {
      setPhoneVerification((prev) => ({
        ...prev,
        isVerifying: false,
        verificationCode: "",
        isCodeSent: false,
        isVerified: false,
        countdown: 0,
        generatedCode: "",
      }));
      setTempPhone(value || "");
    } else if (field === "eMail") {
      setTempEmail(value || "");
    }
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue("");
    setIsEditing(false);

    setPhoneVerification((prev) => ({
      ...prev,
      isVerifying: false,
      verificationCode: "",
      isCodeSent: false,
      isVerified: false,
      countdown: 0,
      generatedCode: "",
    }));
  };

  const handleEditSave = async (field) => {
    try {
      setLoading(true);

      if (field === "phone") {
        // 전화번호 수정 시 SMS 인증 필요
        if (!phoneVerification.isVerified) {
          await swal.info("전화번호 수정을 위해서는 SMS 인증이 필요합니다.");
          return;
        }

        const success = await updateUserPhone(editValue);
        if (success) {
          setPhoneVerification((prev) => ({
            ...prev,
            isVerified: false,
            isCodeSent: false,
            countdown: 0,
            generatedCode: "",
          }));
        }
      } else if (field === "eMail") {
        // 이메일 형식 검증
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!emailRegex.test(editValue)) {
          await swal.error("올바른 이메일 형식이 아닙니다.");
          return;
        }
        const success = await updateUserEmail(editValue);
        if (success) {
          setEditingField(null);
          setEditValue("");
          setIsEditing(false);
        }
      } else {
        // 다른 필드들은 기존 로직 유지
        console.log(`${field} 필드 수정: ${editValue}`);
      }
    } catch (error) {
      console.log("Edit save error: ", error);
      await swal.error("저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyToggle = () => {
    setShowPrivacyModal(true);
  };

  // 개인정보동의방침 동의 처리
  const handlePrivacyChange = async (agree) => {
    try {
      setLoading(true);
      setProfileData((prev) => ({ ...prev, concent: !!agree }));
      const resp = await EmployeeService.upsertAndSync({ concent: !!agree });
      const ok = resp && resp.status === 200;
      if (!ok) {
        await swal.error("동의 처리 중 오류가 발생했습니다.");
        return;
      }
      setUserInfo((prev) => ({ ...prev, concent: !!agree }));
      setShowPrivacyModal(false);
      setIsPrivacyScrolledToBottom(false); // 동의 후 스크롤 상태 초기화
    } catch (error) {
      console.log("Privacy change error: ", error);
      await swal.error("동의 처리 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePrivacyModal = () => {
    setShowPrivacyModal(false);
    setIsPrivacyScrolledToBottom(false);
  };

  const handlePrivacyScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px 여유
    setIsPrivacyScrolledToBottom(isAtBottom);
    if (isAtBottom && !hasEverScrolledToBottom) {
      setHasEverScrolledToBottom(true);
    }
  };

  const handlePhoneInputChange = (e) => {
    const value = e.target.value;
    const formattedValue = Commons.FormatPhoneNumber(value, editValue || "");
    setEditValue(formattedValue);
  };

  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/[^A-Za-z0-9@._%+\-]/g, "");
    setEditValue(cleaned);
  };

  // 사용자 전화번호 수정 함수
  const updateUserPhone = async (newPhone) => {
    try {
      setIsUpdating(true);
      const response = await EmployeeService.upsertAndSync({ tel: newPhone });
      const ok = response?.status === 200 && response?.data;
      if (ok) {
        setProfileData((prev) => ({ ...prev, phone: newPhone }));
        setUserInfo((prev) => ({ ...prev, tel: newPhone }));
        await swal.success(translations.phoneUpdateSuccess);
        // 편집 종료
        setEditingField(null);
        setEditValue("");
        setIsEditing(false);
        return true;
      } else {
        await swal.error(translations.updateFailed);
        return false;
      }
    } catch (error) {
      console.log("전화번호 수정 오류: ", error);
      await swal.error(translations.updateFailed);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // 사용자 이메일 수정 함수
  const updateUserEmail = async (newEmail) => {
    try {
      setIsUpdating(true);
      const response = await EmployeeService.upsertAndSync({ email: newEmail });
      const ok = response?.status === 200 && response?.data;
      if (ok) {
        setProfileData((prev) => ({ ...prev, eMail: newEmail }));
        setUserInfo((prev) => ({ ...prev, email: newEmail, eMail: newEmail }));
        await swal.success(translations.emailUpdateSuccess);
        // 편집 종료
        setEditingField(null);
        setEditValue("");
        setIsEditing(false);
        return true;
      } else {
        await swal.error(translations.updateFailed);
        return false;
      }
    } catch (error) {
      console.log("이메일 수정 오류: ", error);
      await swal.error(translations.updateFailed);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // 편집 가능한 필드 컴포넌트
  const EditableField = ({ field, value, label, type = "text", editable = true }) => {
    const isEditing = editingField === field;

    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        handleEditSave(field);
      } else if (e.key === "Escape") {
        handleEditCancel();
      }
    };

    // 전화번호 필드 특별 처리
    if (field === "phone" && isEditing) {
      return (
        <div className='info-row'>
          <div className='info-label'>
            <FontAwesomeIcon icon={getIconForField(field)} />
            <span>{label}</span>
          </div>
          <div className='info-value'>
            <div className='edit-container'>
              <input
                type='text'
                value={editValue}
                onChange={handlePhoneInputChange}
                onKeyDown={handleKeyPress}
                className='edit-input'
                autoFocus
                placeholder='전화번호를 입력하세요'
                disabled={phoneVerification.isVerified}
              />

              <SmsVerification
                phone={editValue}
                originalPhone={tempPhone}
                translations={translations}
                isVerified={phoneVerification.isVerified}
                onVerified={() => {
                  setPhoneVerification((prev) => ({ ...prev, isVerified: true }));
                }}
              />

              <div className='edit-buttons'>
                <button onClick={() => handleEditSave(field)} className='edit-btn save-btn' title='저장 (Enter)' disabled={field === "phone" && !phoneVerification.isVerified}>
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                <button onClick={handleEditCancel} className='edit-btn cancel-btn' title='취소 (Esc)'>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='info-row'>
        <div className='info-label'>
          <FontAwesomeIcon icon={getIconForField(field)} />
          <span>{label}</span>
        </div>
        <div className='info-value'>
          {isEditing ? (
            <div className='edit-container'>
              {type === "date" ? (
                <input type='date' value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={handleKeyPress} className='edit-input' autoFocus />
              ) : field === "eMail" ? (
                <input
                  type='email'
                  inputMode='email'
                  pattern='^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$'
                  autoCorrect='off'
                  autoCapitalize='none'
                  spellCheck={false}
                  value={editValue}
                  onChange={handleEmailInputChange}
                  onKeyDown={handleKeyPress}
                  className='edit-input'
                  autoFocus
                  placeholder='example@domain.com'
                />
              ) : (
                <input type='text' value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={handleKeyPress} className='edit-input' autoFocus />
              )}
              <div className='edit-buttons'>
                <button onClick={() => handleEditSave(field)} className='edit-btn save-btn' title='저장 (Enter)'>
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                <button onClick={handleEditCancel} className='edit-btn cancel-btn' title='취소 (Esc)'>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>
          ) : (
            <div className={`display-container ${!editable ? "no-edit" : ""}`}>
              <span className='field-value'>
                <AutoTranslate targetLanguage={language.lang}>{type === "date" ? formatDate(value) : value || "정보 없음"}</AutoTranslate>
              </span>
              {editable && (
                <button onClick={() => handleEditStart(field, value)} className='edit-btn edit-start-btn' title='편집'>
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 필드에 따른 아이콘 반환 함수
  const getIconForField = (field) => {
    const iconMap = {
      name: faUser,
      birthDate: faCalendar,
      bloodType: faTint,
      phone: faPhone,
      nationality: faGlobe,
      eMail: faEnvelope,
      concent: faShieldAlt,
    };
    return iconMap[field] || faUser;
  };

  return (
    <div className='wrapper'>
      {loading && <Loading />}
      <div className='profile-container'>
        <Card className='profile-card'>
          <Card.Body>
            <Row>
              <Col lg={4} md={12} className='profile-image-section'>
                <div className='profile-image-container'>
                  <div className='profile-image-wrapper'>
                    {profileData.profileImage ? (
                      <img src={profileData.profileImage} alt='프로필 사진' className='profile-image' />
                    ) : (
                      <div className='profile-image-placeholder'>
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    <label className='image-upload-label' htmlFor='profile-image-upload'>
                      <FontAwesomeIcon icon={faCamera} />
                    </label>
                    <input id='profile-image-upload' type='file' accept='image/*' onChange={handleImageUpload} style={{ display: "none" }} />
                  </div>
                </div>
              </Col>
              <Col lg={8} md={12} className='profile-info-section'>
                <div className='profile-info'>
                  <EditableField field='name' value={profileData.name} label={translations.name} type='text' editable={false} />
                  <EditableField field='birthDate' value={profileData.birthDate} label={translations.birthDate} type='date' editable={false} />
                  <EditableField field='bloodType' value={profileData.bloodType} label={translations.bloodType} type='text' editable={false} />
                  <EditableField field='phone' value={profileData.phone} label={translations.phone} type='text' editable={true} />
                  <EditableField field='nationality' value={profileData.nationality} label={translations.nationality} type='text' editable={false} />
                  <EditableField field='eMail' value={profileData.eMail} label={translations.eMail} type='text' editable={true} />
                  {authorization?.authType !== "b" && (
                    <div className='info-row'>
                      <div className='info-label'>
                        <FontAwesomeIcon icon={faShieldAlt} />
                        <span>{translations.concent}</span>
                      </div>
                      <div className='info-value'>
                        <div className='display-container'>
                          <span className='field-value'>
                            <Badge bg={profileData.concent ? "success" : "danger"} className='privacy-badge'>
                              {profileData.concent ? translations.agreed : translations.notAgreed}
                            </Badge>
                          </span>
                          <button onClick={handlePrivacyToggle} className='edit-btn edit-start-btn' title='상태 변경'>
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* 개인정보처리방침 모달 (사업자는 비표시) */}
      {authorization?.authType !== "b" && (
        <Modal show={showPrivacyModal} onHide={handleClosePrivacyModal} size='lg' centered>
          <Modal.Header closeButton>
            <Modal.Title>{privacyTranslations.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              ref={privacyContentRef}
              className='privacy-content'
              onScroll={handlePrivacyScroll}
              style={{ maxHeight: "60vh", overflowY: "auto" }}
            >
              <h3>{privacyTranslations.title}</h3>

              <div className='privacy-section'>
                <h4>{privacyTranslations.section1.title}</h4>
                <p>{privacyTranslations.section1.description}</p>
                <ul>
                  {privacyTranslations.section1.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className='privacy-section'>
                <h4>{privacyTranslations.section2.title}</h4>
                <p>{privacyTranslations.section2.description}</p>
              </div>

              <div className='privacy-section'>
                <h4>{privacyTranslations.section3.title}</h4>
                <p>{privacyTranslations.section3.description}</p>
              </div>

              <div className='privacy-section'>
                <h4>{privacyTranslations.section4.title}</h4>
                <p>{privacyTranslations.section4.description}</p>
                <ul>
                  {privacyTranslations.section4.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className='privacy-section'>
                <h4>{privacyTranslations.section5.title}</h4>
                <p>{privacyTranslations.section5.description}</p>
                <ul>
                  {privacyTranslations.section5.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {!hasEverScrolledToBottom && !profileData.concent && (
              <small className='text-muted me-2'>
                <AutoTranslate targetLanguage={language.lang}>끝까지 스크롤해주세요</AutoTranslate>
              </small>
            )}
            <Button variant='primary' onClick={() => handlePrivacyChange(true)} disabled={!hasEverScrolledToBottom || profileData.concent}>
              <AutoTranslate targetLanguage={language.lang}>{profileData.concent ? "이미 동의함" : translations.agreed}</AutoTranslate>
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default MyPage;
